import type {
  Asset,
  AssetKind,
  AudioAssetMedia,
  VideoAssetMedia,
} from "@pax/api-contract/assets/v1alpha1";
import { cn, focusHaloFlush, press, TruncatedText } from "@pax/ui";
import { Link } from "@tanstack/react-router";
import {
  AudioLines,
  BadgeCheck,
  EyeOff,
  Film,
  ImageIcon,
  type LucideIcon,
  Map as MapIcon,
  Play,
  Sparkles,
  ThumbsUp,
} from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { assetPage } from "@/features/assets/urls";
import { BasemapThumbnail } from "@/features/basemaps/components/BasemapThumbnail";
import { LoopingVideo } from "@/features/video/components/LoopingVideo";
import { tagLabel } from "@/lib/tags";
import { formatCount, formatDuration, pictureOf, titleOf } from "../media";
import { AssetPicture } from "./AssetPicture";
import { PlayButton, usePlaybackTime, Waveform } from "./Waveform";

export const KIND_ICONS: Record<AssetKind, LucideIcon> = {
  image: ImageIcon,
  video: Film,
  audio: AudioLines,
  basemap: MapIcon,
};

/** Where pressing an asset goes: its page once published, and the editor while it is a draft. */
export function AssetLink({
  asset,
  className,
  onFocus,
  onPointerEnter,
  onPointerLeave,
  children,
}: {
  asset: Asset;
  className?: string;
  onFocus?: () => void;
  onPointerEnter?: () => void;
  onPointerLeave?: () => void;
  children: ReactNode;
}) {
  const handlers = { onFocus, onPointerEnter, onPointerLeave, className };
  return asset.status === "draft" || asset.status === "refused" ? (
    <Link
      to="/assets/id/$assetId/edit"
      params={{ assetId: asset.id }}
      search={{ draft: asset.versionId }}
      {...handlers}
    >
      {children}
    </Link>
  ) : (
    <Link {...assetPage(asset)} {...handlers}>
      {children}
    </Link>
  );
}

/**
 * One asset in a grid, at the shape it was made in.
 *
 * The picture is the asset and the words go under it rather than over it: a
 * mosaic of flags and portraits in every proportion has no edge a caption can
 * sit on without covering something that matters. What is true of every
 * asset — its name, a couple of its tags, who made it — is always there; how
 * well it has been received comes up only when it is pointed at.
 *
 * `trailing` is a control of the grid's own laid over the corner, such as
 * taking the asset back out of a collection.
 */
export function AssetTile({
  asset,
  sizes,
  status,
  trailing,
}: {
  asset: Asset;
  sizes: string;
  /** Show whether it is a draft, on the author's own shelf. */
  status?: boolean;
  trailing?: ReactNode;
}) {
  const body =
    asset.media?.kind === "audio" ? (
      <AudioCard asset={asset} status={status} />
    ) : (
      <PictureCard asset={asset} sizes={sizes} status={status} />
    );
  return (
    <div className="group/tile relative">
      {body}
      {trailing && (
        <div className="absolute top-2 right-2 z-10 opacity-0 transition-opacity duration-200 group-hover/tile:opacity-100 group-focus-within/tile:opacity-100 [@media(hover:none)]:opacity-100">
          {trailing}
        </div>
      )}
    </div>
  );
}

function PictureCard({ asset, sizes, status }: { asset: Asset; sizes: string; status?: boolean }) {
  const picture = pictureOf(asset.media);
  const video = asset.media?.kind === "video" ? asset.media : null;
  const [pointed, setPointed] = useState(false);
  // Once looked at, it stays loaded, so pointing away and back doesn't fetch it again.
  const [lookedAt, setLookedAt] = useState(false);

  return (
    <AssetLink
      asset={asset}
      onFocus={() => setLookedAt(true)}
      onPointerEnter={() => {
        setPointed(true);
        setLookedAt(true);
      }}
      onPointerLeave={() => setPointed(false)}
      className={`group block rounded-xl ${focusHaloFlush} ${press} transition-transform duration-200`}
    >
      <span
        className="relative block overflow-hidden rounded-xl bg-foreground/5 ring-1 ring-foreground/10"
        style={{ aspectRatio: picture ? `${picture.width} / ${picture.height}` : "4 / 3" }}
      >
        {picture ? (
          <AssetPicture
            picture={picture}
            sizes={sizes}
            sensitive={asset.isSensitive === true}
            lookedAt={lookedAt}
            className="transition-transform duration-500 group-hover:scale-[1.02]"
          />
        ) : asset.kind === "basemap" && asset.status === "published" ? (
          <BasemapThumbnail
            assetId={asset.id}
            className="transition-transform duration-500 group-hover:scale-[1.02]"
          />
        ) : (
          <NoFileYet kind={asset.kind} />
        )}
        {video && pointed && <HoverPreview media={video} />}
        <Badges asset={asset} status={status} />
        <span className="absolute right-2 bottom-2 flex items-center gap-1.5">
          {asset.stats && <NetLikes asset={asset} />}
          {video && (
            <span className="flex items-center gap-1 rounded-full bg-black/55 px-2 py-0.5 text-[11px] font-medium text-white tabular-nums">
              <Play aria-hidden className="size-2.5 fill-current" />
              {formatDuration(video.durationSeconds)}
            </span>
          )}
        </span>
      </span>
      <Caption asset={asset} />
    </AssetLink>
  );
}

/**
 * A recording has no picture, so its card is the thing you would do with it:
 * play it, and see where in it you are. Both work without leaving the grid —
 * the rest of the card is the way to its page.
 */
function AudioCard({ asset, status }: { asset: Asset; status?: boolean }) {
  if (asset.media?.kind !== "audio") return null;
  const media = asset.media;
  // The card is a link drawn across the whole of it by its title, so the play
  // button and the waveform can sit above it as controls of their own rather
  // than as buttons inside a link.
  return (
    <div className="group relative rounded-xl bg-foreground/5 p-3 ring-1 ring-foreground/10 transition-colors duration-200 hover:bg-foreground/[0.08]">
      <div className="flex items-center gap-3">
        <PlayButton media={media} className="z-10" />
        <div className="min-w-0 flex-1">
          <AssetLink
            asset={asset}
            className={`block rounded-sm text-sm font-medium after:absolute after:inset-0 after:rounded-xl ${focusHaloFlush}`}
          >
            <TruncatedText text={titleOf(asset)} lines={2} className="text-pretty" />
          </AssetLink>
          <AudioDetail media={media} author={asset.owner.displayName} />
        </div>
      </div>
      <div className="relative z-10 mt-3 h-10">
        <Waveform media={media} bars={48} />
      </div>
      {/* The card's foot, where a sensitive recording says so: beside the name
          there isn't room for both the label and the length on a phone. */}
      <div className="mt-2 flex min-h-5 items-start justify-between gap-2">
        <span className="flex min-w-0 items-start gap-1.5">
          {asset.isSensitive && <SensitiveLabel />}
          <TruncatedText
            text={shownTags(asset).join(" · ")}
            lines={2}
            className="min-w-0 text-xs text-muted-foreground"
          />
        </span>
        {asset.stats && <NetLikes asset={asset} quiet />}
      </div>
      <Badges asset={asset} status={status} inline />
    </div>
  );
}

/**
 * Two of an asset's tags, each held together: the spaces inside one tag don't break, so a line
 * that has to break does it between tags rather than inside one.
 */
export function shownTags(asset: Asset): string[] {
  return asset.tags.slice(0, 2).map(tag => tagLabel(tag).replaceAll(" ", "\u00a0"));
}

/** The line under a picture's name: two of its tags and who made it. */
export function captionDetail(asset: Asset): string {
  return [...shownTags(asset), asset.owner.displayName].join(" · ");
}

/** A recording's time and who made it, the time counting up once it is playing. */
function AudioDetail({ media, author }: { media: AudioAssetMedia; author: string }) {
  return (
    <TruncatedText
      text={`${usePlaybackTime(media)} · ${author}`}
      lines={2}
      className="mt-0.5 text-xs text-muted-foreground tabular-nums"
    />
  );
}

/**
 * A picture's name and its line of tags and author, one line each, cut short rather than wrapped,
 * so pictures of one shape always end level in a row.
 */
function Caption({ asset }: { asset: Asset }) {
  const Icon = KIND_ICONS[asset.kind];
  return (
    <span className="block px-0.5 pt-2 pb-1">
      <TruncatedText text={titleOf(asset)} lines={1} className="text-sm font-medium" />
      <span className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
        <Icon aria-hidden className="size-3 shrink-0" />
        <TruncatedText text={captionDetail(asset)} lines={1} className="min-w-0 flex-1" />
      </span>
    </span>
  );
}

/**
 * Two facts about where an asset came from, each a mark rather than a word:
 * staff checked it, or detectors think a model made it. The words are the
 * marks' names, for whoever points at them or reads them aloud.
 */
function Badges({ asset, status, inline }: { asset: Asset; status?: boolean; inline?: boolean }) {
  const marks: { key: string; node: ReactNode }[] = [];
  if (status && asset.status === "draft") {
    marks.push({
      key: "draft",
      node: (
        <span className="rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-medium text-white">
          Draft
        </span>
      ),
    });
  }
  const shelfMark = asset.removedAt ? "Taken down" : asset.archivedAt ? "Archived" : null;
  if (status && shelfMark) {
    marks.push({
      key: "shelf",
      node: (
        <span className="rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-medium text-white">
          {shelfMark}
        </span>
      ),
    });
  }
  if (asset.isHumanVerified) {
    marks.push({
      key: "verified",
      node: (
        <span
          title="Human verified: reviewed by a Pax moderator for policy and metadata fit"
          role="img"
          aria-label="Human verified"
          className="flex size-6 items-center justify-center rounded-full bg-black/55 text-white"
        >
          <BadgeCheck aria-hidden className="size-3.5" />
        </span>
      ),
    });
  }
  if (asset.isAiGenerated) {
    marks.push({
      key: "ai",
      node: (
        <span
          title="AI generated: our detectors flagged this as likely made with generative AI"
          role="img"
          aria-label="AI generated"
          className="flex size-6 items-center justify-center rounded-full bg-black/55 text-white"
        >
          <Sparkles aria-hidden className="size-3.5" />
        </span>
      ),
    });
  }
  if (marks.length === 0) return null;
  return (
    <span
      className={cn(
        "flex items-center gap-1",
        inline ? "absolute top-3 right-3" : "absolute top-2 left-2",
      )}
    >
      {marks.map(mark => (
        <span key={mark.key}>{mark.node}</span>
      ))}
    </span>
  );
}

/**
 * What a sensitive recording carries in place of a blur: there's no picture to hide, so it says
 * so in words before anyone presses play.
 */
export function SensitiveLabel() {
  return (
    <span className="flex shrink-0 items-center gap-1 rounded-full bg-foreground/10 px-1.5 py-px text-[10px] font-medium text-foreground">
      <EyeOff aria-hidden className="size-2.5" />
      Sensitive
    </span>
  );
}

/** Likes less dislikes, which is what "top rated" orders by. */
function NetLikes({ asset, quiet }: { asset: Asset; quiet?: boolean }) {
  if (!asset.stats) return null;
  const net = asset.stats.likeCount - asset.stats.dislikeCount;
  return (
    <span
      className={cn(
        "flex items-center gap-1 text-[11px] font-medium tabular-nums transition-opacity duration-200",
        quiet
          ? "text-muted-foreground"
          : "rounded-full bg-black/55 px-2 py-0.5 text-white opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 [@media(hover:none)]:opacity-100",
      )}
    >
      <ThumbsUp aria-hidden className="size-2.5" />
      {net < 0 ? `−${formatCount(-net)}` : formatCount(net)}
    </span>
  );
}

function NoFileYet({ kind }: { kind: AssetKind }) {
  const Icon = KIND_ICONS[kind];
  return (
    <span className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground">
      <Icon aria-hidden className="size-6" strokeWidth={1.5} />
      <span className="text-xs">No file yet</span>
    </span>
  );
}

/** How long the pointer rests on a video before it starts moving. Passing over it is not looking. */
const PREVIEW_DWELL_MS = 250;

/**
 * The video itself, silent and looping, over its poster while it is pointed
 * at, and only after a moment's rest, so sweeping across a wall of videos
 * doesn't start downloading every one of them. Where the browser can't decode
 * it, the poster stays.
 */
function HoverPreview({ media }: { media: VideoAssetMedia }) {
  const [dwelled, setDwelled] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setDwelled(true), PREVIEW_DWELL_MS);
    return () => window.clearTimeout(timer);
  }, []);
  if (!dwelled) return null;
  return <LoopingVideo video={media} className="absolute inset-0 duration-300" />;
}
